import React from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Building2,
  Check,
  CreditCard,
  ShieldCheck,
  Users,
  Wrench,
} from "lucide-react";
import {
  CtaSection,
  PageHero,
  Section,
  btnOutlineDark,
  btnPrimary,
  cardClass,
  cardIconClass,
  cardTitleClass,
  useAuthActions,
} from "../components/SiteUI";

const Features = () => {
  const navigate = useNavigate();
  const { openSignup } = useAuthActions();

  const featureGroups = [
    {
      title: "Portfolio Management",
      icon: Building2,
      items: ["Property registry", "Unit inventory", "Owner records", "Document center"],
    },
    {
      title: "Tenant and Lease",
      icon: Users,
      items: ["Tenant profiles", "Lease terms tracking", "Renewal reminders", "Communication log"],
    },
    {
      title: "Payments and Billing",
      icon: CreditCard,
      items: ["Invoice generation", "Payment status", "Late fee handling", "Receipts history"],
    },
    {
      title: "Maintenance",
      icon: Wrench,
      items: ["Request intake", "Task assignment", "Status updates", "Cost tracking"],
    },
    {
      title: "Reports",
      icon: BarChart3,
      items: ["Occupancy trends", "Cashflow snapshots", "Arrears reports", "Export tools"],
    },
    {
      title: "Security and Access",
      icon: ShieldCheck,
      items: ["Role-based permissions", "User activity logs", "Secure login", "Data reliability"],
    },
  ];

  return (
    <div className="bg-teal-50">
      <PageHero
        eyebrow="Features"
        title="Everything Needed to Run Rental Operations"
        description="Gamrent combines operations, finance, and reporting in one place, with account activation controlled by Gamrent admin after payment confirmation."
      />

      <Section>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featureGroups.map((group, idx) => (
            <article key={group.title} className={`reveal reveal-delay-${(idx % 3) + 1} lift ${cardClass}`}>
              <div className="flex items-center gap-3">
                <div className={cardIconClass} aria-hidden="true">
                  <group.icon className="w-5 h-5" />
                </div>
                <h2 className={cardTitleClass}>{group.title}</h2>
              </div>
              <ul className="mt-5 space-y-3">
                {group.items.map((item) => (
                  <li key={item} className="text-sm text-teal-800 flex items-start gap-3">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-teal-700" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </Section>

      <CtaSection
        title="Built for teams that manage growth"
        description="Whether you oversee 10 properties or 1,000 units, workflows stay clear and your team stays aligned from signup through dashboard activation. After signup, share payment evidence with Gamrent admin to get access enabled."
      >
        <button onClick={openSignup} className={btnPrimary}>
          Create Account
        </button>
        <button onClick={() => navigate("/pricing")} className={btnOutlineDark}>
          View Pricing
        </button>
      </CtaSection>
    </div>
  );
};

export default Features;
